import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  subscribeMeetingIntelEvents,
  type MeetingIntelEvent,
} from "../meetingIntelligenceRealtime";
import type { ReadAiMeeting } from "../meetingIntelligence.types";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import {
  MEETING_INTEL_SERVER_CAP,
  rowsReturned,
  isAtCap,
  type LoadCap,
} from "shared/truncation";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { openActionItems } from "../actionItemClose";
import { isSessionEnded } from "stores/sessionGuard";

export type MeetingProcessedFilter = "all" | "processed" | "unprocessed";

/** Mapping-state filter: mapped (has a client), still in the review queue, or
 *  human-marked internal (no client, terminal). */
export type MeetingMappingFilter = "all" | "mapped" | "needs_review" | "internal";

export type MeetingSortKey =
  | "title"
  | "clientName"
  | "ownerName"
  | "startTime"
  | "actionItemCount"
  | "createdAt";

export type MeetingSortDir = SortDir;

/** Sort keys that read best newest/biggest-first on first click. */
const DESC_FIRST: ReadonlySet<MeetingSortKey> = new Set([
  "startTime",
  "createdAt",
  "actionItemCount",
]);

/** A client that appears in the loaded meetings — the client filter's options. */
export interface MeetingClientOption {
  id: string;
  name: string;
}

/**
 * One CSV row of the meetings export — the on-screen table, flattened. Keys are
 * the `ColumnConfig.key` values in `Meetings.Header.tsx`; every value is already
 * a primitive so the CSV writer never has to guess at a shape.
 */
export interface MeetingExportRow extends Record<string, unknown> {
  id: number;
  title: string;
  client: string;
  mappingStatus: string;
  owner: string;
  ownerEmail: string;
  startTime: string;
  endTime: string;
  status: string;
  processingError: string;
  actionItemCount: number;
  processedAt: string;
  createdAt: string;
}

/** ISO-8601 for a spreadsheet; "" for missing/unparseable. Deliberately NOT the
 *  export toolkit's `format: "date"`, which drops the time component. */
const isoOrEmpty = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
};

/** The same three states the table's status chip renders. */
const meetingStatus = (meeting: ReadAiMeeting): string =>
  meeting.processingError ? "error" : meeting.processed ? "processed" : "pending";

/**
 * Route ViewStore for the Meetings list (ingested Read.ai sessions).
 *
 * Rule (web CLAUDE.md): route ViewStores are plain classes with
 * `makeAutoObservable` and do NOT extend ViewStore. The read-ai services are
 * read through the registered `read-ai-meetings` ServiceStore (see
 * serviceStores/meetingIntelligence.stores.ts); per the MobX rule the meeting
 * list is a computed view over that store's entity cache, not a local array.
 *
 * Meetings arrive via the Read.ai webhook (no create UI). The store subscribes
 * to the Meeting Intelligence live channel and reloads whenever a transcript is
 * received or a meeting finishes processing, so the list stays current without
 * a manual refresh.
 */
class MeetingsStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Header filters
  searchQuery = "";
  processedFilter: MeetingProcessedFilter = "all";
  /** "" = all clients; a clients.id narrows to that client's meetings. */
  clientFilter = "";
  mappingFilter: MeetingMappingFilter = "all";

  // Column sort (applied client-side over the loaded set, like the filters)
  sortKey: MeetingSortKey = "createdAt";
  sortDir: MeetingSortDir = "desc";

  /**
   * Rows the last successful load returned. Null before the first load, and
   * left alone when a load resolves nothing (a swallowed transport error), so a
   * failed refresh cannot quietly retract a cap the UI already reported.
   */
  loadedRowCount: number | null = null;

  private unsubscribe: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `read-ai-meetings` ServiceStore. */
  private get meetingsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("read-ai-meetings") ?? this.rootStore.readAiMeetingsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * Meeting list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get meetings(): ReadAiMeeting[] {
    return (this.meetingsService?.entities ?? []) as unknown as ReadAiMeeting[];
  }

  /** True when a search or any header filter is set. */
  get hasActiveFilter(): boolean {
    return (
      this.searchQuery.trim().length > 0 ||
      this.processedFilter !== "all" ||
      this.clientFilter !== "" ||
      this.mappingFilter !== "all"
    );
  }

  get filteredMeetings(): ReadAiMeeting[] {
    let result = this.meetings;

    if (this.processedFilter === "processed") {
      result = result.filter((m) => !!m.processed);
    } else if (this.processedFilter === "unprocessed") {
      result = result.filter((m) => !m.processed);
    }

    if (this.clientFilter) {
      result = result.filter((m) => m.clientId === this.clientFilter);
    }

    if (this.mappingFilter === "mapped") {
      result = result.filter((m) => !!m.clientId);
    } else if (this.mappingFilter === "needs_review") {
      result = result.filter((m) => m.mappingStatus === "needs_review");
    } else if (this.mappingFilter === "internal") {
      result = result.filter((m) => m.mappingStatus === "internal");
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (m) =>
          m.title?.toLowerCase().includes(q) ||
          m.clientName?.toLowerCase().includes(q) ||
          m.ownerName?.toLowerCase().includes(q) ||
          m.ownerEmail?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * sort last in either direction so "no start time" never floats to the top.
   */
  get sortedMeetings(): ReadAiMeeting[] {
    const key = this.sortKey;
    const valueOf = (m: ReadAiMeeting): SortValue => {
      switch (key) {
        case "title":
          return textValue(m.title);
        case "clientName":
          return textValue(m.clientName);
        case "ownerName":
          return textValue(m.ownerName ?? m.ownerEmail);
        case "startTime":
          return dateValue(m.startTime);
        case "createdAt":
          return dateValue(m.createdAt);
        case "actionItemCount":
          return openActionItems(m);
      }
    };
    return sortRows(this.filteredMeetings, valueOf, this.sortDir);
  }

  /** Distinct clients present in the loaded meetings, for the client filter. */
  get clientOptions(): MeetingClientOption[] {
    const byId = new Map<string, string>();
    for (const m of this.meetings) {
      if (m.clientId && !byId.has(m.clientId)) {
        byId.set(m.clientId, m.clientName || m.clientId);
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // ---- Load cap (see ../loadCap.ts) ---------------------------------------

  /** What the last load asked for vs. what came back. */
  get loadCap(): LoadCap {
    return {
      requested: MEETING_INTEL_SERVER_CAP,
      returned: this.loadedRowCount,
    };
  }

  /**
   * True when the loaded set is at the server's cap — i.e. every count below is
   * a LOWER BOUND, not a total. `read-ai-meetings` returns a bare array with no
   * `total` and honours no `$skip`, so this is the only truncation signal there
   * is; see ../loadCap.ts for the full reasoning.
   */
  get isTruncated(): boolean {
    return isAtCap(this.loadCap);
  }

  /**
   * Meetings currently in the cache. Named "total" for historical reasons and
   * kept for the callers that use it — it is the LOADED count, which equals the
   * true total only while `isTruncated` is false.
   */
  get totalCount(): number {
    return this.meetings.length;
  }

  get processedCount(): number {
    return this.meetings.filter((m) => !!m.processed).length;
  }

  get unprocessedCount(): number {
    return this.meetings.filter((m) => !m.processed).length;
  }

  get withActionItemsCount(): number {
    return this.meetings.filter((m) => openActionItems(m) > 0).length;
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * The CSV shape of WHAT IS ON SCREEN — `sortedMeetings`, so the file carries
   * the active filters and the active column sort. Exporting a fresh unfiltered
   * fetch would silently disagree with the table the operator is looking at.
   * Pure: no I/O, no side effects, safe to snapshot in a test.
   */
  get exportRows(): MeetingExportRow[] {
    return this.sortedMeetings.map((m) => ({
      id: m.id,
      title: m.title ?? "",
      client: m.clientName ?? "",
      mappingStatus: m.mappingStatus ?? "",
      owner: m.ownerName ?? "",
      ownerEmail: m.ownerEmail ?? "",
      startTime: isoOrEmpty(m.startTime),
      endTime: isoOrEmpty(m.endTime),
      status: meetingStatus(m),
      processingError: m.processingError ?? "",
      actionItemCount: m.actionItemCount ?? 0,
      processedAt: isoOrEmpty(m.processedAt),
      createdAt: isoOrEmpty(m.createdAt),
    }));
  }

  /** `exportToCsv` throws on an empty set, so the button is disabled instead. */
  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setProcessedFilter(filter: MeetingProcessedFilter) {
    this.processedFilter = filter;
  }

  setClientFilter(clientId: string) {
    this.clientFilter = clientId;
  }

  setMappingFilter(filter: MeetingMappingFilter) {
    this.mappingFilter = filter;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (dates/counts newest/biggest-first, text A→Z). */
  setSort(key: MeetingSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.ensureSubscribed();
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `meetings` reads.
      // MEETING_INTEL_SERVER_CAP is the service's own hard clamp, not a
      // preference — `read-ai-meetings` does `Math.min($limit, 500)`, returns a
      // bare array with no total, and ignores $skip, so this is one page and
      // there is no second one to ask for. Record how many rows actually came
      // back: returning exactly the cap is what tells the UI the set is
      // truncated (see ../loadCap.ts).
      // findOrThrow: a swallowed failure rendered "No meetings ingested yet"
      // with every tile at 0.
      const response = await findOrThrow(this.meetingsService, {
        query: { $limit: MEETING_INTEL_SERVER_CAP, $sort: { createdAt: -1 } },
      });
      const returned = rowsReturned(response);
      if (returned !== null) {
        runInAction(() => {
          this.loadedRowCount = returned;
        });
      }
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load meetings");
      });
      console.error("Failed to load meetings:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / realtime callbacks. */
  async reload() {
    await this.load();
  }

  // ---- Realtime -----------------------------------------------------------

  private ensureSubscribed() {
    if (this.unsubscribe) return;
    // Shared realtime bus (src/realtime) — auth is sourced internally from the
    // AuthStore. This subscription used to be silently inert because it was
    // made with no token; that state is no longer expressible.
    this.unsubscribe = subscribeMeetingIntelEvents(
      this.rootStore,
      (event: MeetingIntelEvent) => {
        if (
          event.kind === "meeting-processed" ||
          event.kind === "transcript-received" ||
          // A meeting mapped to a client (2026-11-02): its Client column and
          // mapping status change, and under Gantry authority the reprocess
          // the mapping runs announces nothing else (Codex r1).
          event.kind === "meeting-mapped"
        ) {
          void this.reload();
        }
      }
    );
  }

  /** Tear down the realtime subscription (call on route exit). */
  dispose() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.processedFilter = "all";
    this.clientFilter = "";
    this.mappingFilter = "all";
    this.sortKey = "createdAt";
    this.sortDir = "desc";
    this.loadedRowCount = null;
    this.unsubscribe = null;
  }
}

export default MeetingsStore;
